import {
  AutoComplete,
  Col,
  DatePicker,
  Row,
  Table,
  TablePaginationConfig,
  Tag,
} from "antd";
import React, { useEffect, useState, useRef } from "react";
import { MultiSelect } from "primereact/multiselect";
import "../../AntdControlComponent/TableComponent/TableComponentCSS.css";
import "../Table.css";
import SortingButton from "../../ButtonComponents/SortingButton";
import { sorterFunc } from "../../../Helper/SortingFunction";
import moment, { Moment } from "moment";
import { Button } from "primereact/button";
import {
  exportExcel,
  reconStructionForExport,
} from "../../../Helper/ExportExcel";
import { updateDynamic } from "../../../Services/DynamicService";
import { useLocation } from "react-router";
import { useUserContext } from "../../../Context/UserContext";
import { IPaging } from "../../../IRequestModel/IPaginationOptionModel";
import useMasterSetting from "../../../hooks/useMasterSetting";
import { ColumnsType } from "antd/lib/table";
import {
  checkedForDataTypes,
  formatData,
  getColumnSearchProps,
} from "../../../Helper/TableHelper";
import { ColumnFilterItem, FilterValue } from "antd/lib/table/interface";
import { data } from "jquery";
import useAlert from "../../../hooks/useAlert";
import * as XLSX from "xlsx";

const { RangePicker } = DatePicker;

type Props = {
  tableName: string;
  dataSource: any[];
  customColumns?: any[];
  setItemsCount?: (value: number) => void;
  canExport: boolean;
  setLoad?: (value: boolean) => void;
  actionBodyTemplate?: any;
  customImportFileButton?: any;
  rowClickAction?: any;
  canEdit?: any;
  toast?: any;
  loading?: boolean;
  reloadData?: (apiName: string) => void;
  VersionTempVCProps?: any;
  paginationOption?: TablePaginationConfig;
  onPageChange?: (paginationValue: IPaging) => void;
};

type ITableFilter = {
  key: string;
  type: string;
  sortType: "dec" | "asc" | null;
  value: any;
};

export type ColumnsProps = {
  key: string;
  label: string;
  type: string;
  option: any[];
  maxNumberValue?: number;
  decimal?: number;
  symbol?: string;
  dateFormat?: string;
};

const DynamicTable = ({
  tableName,
  dataSource,
  customColumns,
  canExport,
  setItemsCount,
  setLoad,
  canEdit,
  actionBodyTemplate,
  rowClickAction,
  reloadData,
  customImportFileButton,
  loading,
  paginationOption,
  onPageChange,
}: Props) => {
  const dataKey = customColumns
    ? customColumns.map((e) => e.key)
    : Object.keys(dataSource[0] ?? {});
  const { toggleAlert } = useAlert();
  const [hoverAction, setHoverAction] = useState<boolean>(false);
  const [columnsProps, setColumnsProps] = useState<ColumnsProps[]>([]);
  const [filterState, setFilterState] = useState<
    Record<string, FilterValue | null>
  >({});
  const [searchedData, setSearchedData] = useState<any[]>([]);
  const { masterSetting, getMasterSetting } = useMasterSetting();
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [showCols, setShowCols] = useState<any>();
  const [userData] = useUserContext();
  const [importLoading, setImportLoading] = useState<boolean>(false);
  const fileInputRef = useRef<any>(null);

  useEffect(() => {
    initFunc();
    if (customColumns) {
      setSearchedData(
        dataSource.map((e, idx) => {
          e.rowIdx = idx;
          return e;
        })
      );
    } else {
      setSearchedData(dataSource);
    }
  }, [dataSource]);

  const initFunc = async (selectedCols?: string[]) => {
    let keyTypes: ColumnsProps[] = [];
    let _selectedCols: string[] = [];
    const _masterSetting: any = await getMasterSetting();

    if (customColumns) {
      keyTypes = [...checkedForDataTypes(dataKey, dataSource)];

      keyTypes.forEach((e) => {
        const customCol = customColumns.find(
          (customCol) => e.key === customCol.key
        );

        if (customCol) {
          _selectedCols.push(e.key);
          e.label = customCol.label;
          e.symbol = customCol.symbol;
          e.decimal = Number(customCol.Decimal ?? 2);
          e.type =
            customCol.FieldTypeFilterDynamic === "c"
              ? "number"
              : customCol.FieldTypeFilterDynamic === "d"
              ? "date"
              : customCol.FieldTypeFilterStatic === "Datetime"
              ? "datetime"
              : customCol.FieldTypeFilterDynamic === "at"
              ? "file"
              : "string";
          if (e.type === "date" && _masterSetting?.FormatDate?.Value2) {
            e.dateFormat = _masterSetting?.FormatDate?.Value2;
          } else if (customCol.FieldTypeFilterDynamic === "d") {
            const symbol =
              customCol.symbol && customCol.symbol !== ""
                ? customCol.symbol
                : " ";
            e.dateFormat = "DD" + symbol + "MMM" + symbol + "YYYY";
          } else if (e.type === "datetime") {
            e.dateFormat =
              _masterSetting?.FormatDate?.Value1 || "DD MMM yyyy hh:mm:ss";
          }

          if (e.type === "number") {
            const optionValue: any = e.option.map((option) => {
              return option?.value
                ? Number(option?.value?.replaceAll(",", ""))
                : 0;
            });
            e.maxNumberValue = Math.max(optionValue);
          }
        }
      });

      if (!selectedCols) {
        selectedCols = _selectedCols.filter(
          (key: string) => !key?.toLowerCase()?.includes("id")
        );
      }
    } else {
      if (actionBodyTemplate) {
        keyTypes = [
          { key: "operation", label: "", type: "action", option: [] },
          ...checkedForDataTypes(
            dataKey,
            dataSource,
            _masterSetting?.FormatDate?.Value1?.replaceAll("d", "D")
          ),
        ];
      } else {
        keyTypes = [
          ...checkedForDataTypes(
            dataKey,
            dataSource,
            _masterSetting?.FormatDate?.Value1?.replaceAll("d", "D")
          ),
        ];
      }

      formatData(dataSource, keyTypes);

      if (!selectedCols) {
        selectedCols = dataKey.filter(
          (key: string) => !key?.toLowerCase()?.includes("id")
        );
      }
    }

    setColumnsProps(
      keyTypes.filter(
        (e) =>
          e.key === "operation" ||
          (selectedCols?.includes(e.key) &&
            !["isEditing", "isPublishVersion"].includes(e.key))
      ) ?? []
    );
    setShowCols(selectedCols ?? []);
  };

  const sorterDate = (a: Moment | null, b: Moment | null) => {
    if (a === null && b === null) return 0;
    if (a === null) return -1;
    if (b === null) return 1;

    if (!a.isValid() && !b.isValid()) return 0;
    if (!a.isValid()) return -1;
    if (!b.isValid()) return 1;

    if (a.isBefore(b)) return -1;
    if (a.isAfter(b)) return 1;
    return 0;
  };

  const renderColumnBody = (value: any, colProp: ColumnsProps, record: any) => {
    switch (colProp.type) {
      case "number":
        if (colProp.symbol) {
          return value
            ? Number(value.toString()?.replaceAll(",", "")).toLocaleString(
                undefined,
                { minimumFractionDigits: colProp.decimal }
              ) + colProp.symbol
            : "";
        } else {
          return value
            ? Number(value.toString()?.replaceAll(",", "")).toLocaleString(
                undefined,
                { minimumFractionDigits: colProp.decimal }
              )
            : "";
        }

      case "date":
        const date = value as Moment;
        try {
          
          return date?.isValid() ? date.format(colProp.dateFormat) : "";
        } catch (error) {
          return date;
        }
      case "datetime":
        const datetime = value as Moment;
        try {
          
          const datetime = moment(value);
          if (datetime.isValid()) {
            // กำหนดรูปแบบวันที่ถ้าไม่ได้กำหนดให้ใช้รูปแบบปริยาย
            const format = colProp.dateFormat ? colProp.dateFormat.replace("d", "D") : "DD MMM YYYY hh:mm:ss";
            return datetime.format(format);
          } else {
            return value ??"Invalid date"
          }

        } catch (error) {
          return datetime.format("DD MMM yyyy hh:mm:ss");
        }

      case "file":
        return value;

      default:
        if (colProp.key === "TemplateName") {
          return (
            <>
              {value.toString()}
              {record.isPublishVersion === "true" && (
                <Tag color="#007bff" style={{ borderRadius: "6px" }}>
                  Publish
                </Tag>
              )}
              {record.isEditing === "true" && (
                <Tag color="#dc3545" style={{ borderRadius: "6px" }}>
                  Editing
                </Tag>
              )}
            </>
          );
        } else if (colProp.key === "AmountFrom_AmountTo") {
          const dd = value?.split(":");
          let htmlBody: any[] = [];
          dd.forEach((data: any, idx: number) => {
            if (idx < 4) {
              htmlBody.push(<p>{data}</p>);
            } else {
              htmlBody.push(<p>.......</p>);
            }
          });
          return <>{htmlBody}</>;
        } else {
          return value?.toString();
        }
    }

  };

  const onExportClick = async () => {
    const columnHeader = columnsProps.map((col) => ({
      label: col.label,
      key: col.key,
    }));
    setExportLoading(true);
    exportExcel(
      columnHeader,
      tableName,
      reconStructionForExport(searchedData, columnsProps),
      setExportLoading
    );
    setExportLoading(false);
  };

  const onImportExcel = async (e: any) => {
    setImportLoading(true);
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0]; // sheet อันแรก
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      console.log("jsonData", jsonData);

      // const res = await ImportUpdateApproveMatrix(jsonData);

      // if (res.message === "success") {
      //   toggleAlert({
      //     description: `${res.message}`,
      //     message: `Import Success`,
      //     type: "success",
      //   });
      // }
    } catch (error) {
      // toggleAlert({
      //   description: `${res.message}`,
      //   message: `Import Error`,
      //   type: "error",
      // });
      console.error(error);
    } finally {
      setImportLoading(false);
    }
  };

  // function reconStructionForExport() {
  //   let dataExport: any[] = [];
  //   try {
  //     searchedData.forEach((currentData) => {
  //       let exportRow: any = {};
  //       columnsProps.forEach((col: ColumnsProps) => {
  //         if (!showCols.includes(col.key)) return;
  //         if (!currentData[col.key]) return "";

  //         if (col.type === "date" || col.type === "datetime") {
  //           console.log(col);

  //           exportRow[col.key] = currentData[col.key].format(col.dateFormat);
  //         } else if (col.type === "number") {
  //           exportRow[col.key] = Number(currentData[col.key]);
  //         } else {
  //           exportRow[col.key] = currentData[col.key];
  //         }
  //       });
  //       if (JSON.stringify(exportRow) !== "{}") {
  //         dataExport.push(exportRow);
  //       }
  //     });
  //   } catch (error) {
  //     console.error("table=>error", error);
  //   }
  //   return dataExport;
  // }

  const importExcel = async (e: any) => {
    const file = e.target.files[0];
    console.log("file", file);

    try {
      // import("xlsx").then((xlsx) => {
      //   const reader = new FileReader();
      //   try {
      //     reader.onload = async (e: any) => {
      //       const wb = xlsx.read(e.target.result, { type: "array" });
      //       const wsname = wb.SheetNames[0];
      //       const ws = wb.Sheets[wsname];
      //       const data = xlsx.utils.sheet_to_json(ws, { header: 1 });
      //       const cols: any = data[0];
      //       data.shift();
      //       let _importedData = data.map((d: any) => {
      //         return cols.reduce((obj: any, c: any, i: any) => {
      //           obj[c] = d[i];
      //           return obj;
      //         }, {});
      //       });
      //       if (_importedData.length !== 0) {
      //         let res = null;
      //         for (let i = 0; i < _importedData.length; i++) {
      //           const element = _importedData[i];
      //           var apiNamereplace = tableName?.replace("/GetAll", "");
      //           if (
      //             !_importedData[i][apiNamereplace + "Id"] ||
      //             _importedData[i][apiNamereplace + "Id"] === ""
      //           ) {
      //             _importedData[i].CreatedBy = userData.EmployeeId.toString();
      //             _importedData[i].ModifiedBy = userData.EmployeeId.toString();
      //           } else {
      //             _importedData[i].ModifiedBy = userData.EmployeeId.toString();
      //           }
      //           setImportLoading(true);

      //           res = await updateDynamic(apiNamereplace, element);
      //         }

      //         if (res.result === "success") {
      //           if (reloadData) {
      //             reloadData(tableName);
      //           }
      //         }
      //         setImportLoading(false);
      //       }
      //     };

      //     reader.readAsArrayBuffer(file);
      //   } catch (error) {
      setImportLoading(false);
      //   }
      // });
    } catch (error) {
      setImportLoading(false);
    }
    e.target.value = null;
  };

  const columns: ColumnsType<any> = columnsProps
    .filter((e) => showCols?.includes(e.key) || e.key === "operation")
    ?.map((colProp) => {
      const key = colProp.key;

      if (canEdit && key === "operation") {
        return {
          key: "operation",
          align: "center",
          fixed: "left",
          width: 25,
          render: (_: any, record: any) => {
            if (actionBodyTemplate) {
              return (
                <div
                  onMouseOver={(e: any) => setHoverAction(true)}
                  onMouseLeave={(e: any) => setHoverAction(false)}
                >
                  {actionBodyTemplate(record)}
                </div>
              );
            }
          },
        };
      }

      return {
        title: <span className="headtext-form">{colProp.label}</span>,
        dataIndex: key,
        key: key,
        align: colProp.type === "number" ? "right" : "left",
        width: 250,
        render: (value: any, record: any, index: number) =>
          renderColumnBody(value, colProp, record),
        sorter: (a: any, b: any) => {
          return colProp.type === "date" || colProp.type === "datetime"
            ? sorterDate(a[key], b[key])
            : a[key]?.toString().localeCompare(b[key]?.toString());
        },
        ...getColumnSearchProps(colProp, dataSource, filterState, columnsProps),
      };
    });

  const onTriggerImportfile = () => {
    fileInputRef.current?.click();
  };
  return (
    <div className="dynamictable-container">
      <Row className="dynamictable-header" gutter={[0, 5]}>
        <Col xs={24} sm={24} md={10} lg={8} xl={8}>
          <MultiSelect
            value={showCols}
            options={
              customColumns ??
              dataKey.filter((e) => !e.toLowerCase().endsWith("id"))
            }
            onChange={(e) => {
              setShowCols(e.value);
            }}
            showClear
            placeholder="Select columns"
            style={{ width: "50%" }}
            optionLabel={customColumns ? "label" : undefined}
            optionValue={customColumns ? "key" : undefined}
          />
        </Col>
        {canExport && (
          <Col>
            <Row>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "12px",
                  width: "100%",
                  alignContent: "center",
                  border: 0,
                }}
              >
                {/* <Button
                  label="Export to excel"
                  icon="pi pi-file-excel"
                  data-pr-tooltip="XLS"
                  loading={exportLoading}
                  onClick={onExportClick}
                  style={{
                    height: "38px",
                    background: " #28a745",
                    border: "1px solid #28a745",
                    color: "#ffffff",
                    fontSize: "1em",
                    borderRadius: "6px",
                    margin: " 0px 11px 5px 0px",
                    justifyContent: "center",
                  }}
                /> */}

                <Button
                  type="button"
                  label="Export to excel"
                  icon="pi pi-file-excel"
                  data-pr-tooltip="XLS"
                  loading={exportLoading}
                  onClick={onExportClick}
                  style={{
                    background: "#28a745",
                    border: "1px solid #28a745",
                    color: "#ffffff",
                    fontSize: "14px",
                    borderRadius: "6px",
                    justifyContent: "center",
                  }}
                />

                {!location.pathname.includes("DynamicReport") &&
                  !location.search?.includes("DelegateList") &&
                  !location.search?.includes("TimeStamp") &&
                  // !location.search?.includes("MasterData") &&
                  // !location.search?.includes("Employee") &&
                  // !location.search?.includes("Department") &&
                  // !location.search?.includes("Section") &&
                  // !location.search?.includes("Division") &&
                  // !location.search?.includes("Organization") &&
                  // !location.search?.includes("Position") &&
                  // !location.search?.includes("PositionLevel") &&
                  !location.search?.includes("wolfaccount") && (
                    <>
                      {customImportFileButton ? (
                        customImportFileButton
                      ) : (
                        <>
                          <Button
                            type="button"
                            label="Import form excel"
                            icon="pi pi-file-excel"
                            data-pr-tooltip="XLS"
                            loading={importLoading}
                            onClick={onTriggerImportfile}
                            style={{
                              background: "#28a745",
                              border: "1px solid #28a745",
                              color: "#ffffff",
                              fontSize: "14px",
                              borderRadius: "6px",
                              justifyContent: "center",
                            }}
                          />

                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={onImportExcel}
                            accept=".xlsx, .xls"
                            style={{ display: "none" }}
                          />

                          {/* <input
                          type="file"
                          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                          onChange={importExcel}
                          style={{ display: "none" }}
                          id="file"
                        />
                        <label
                          htmlFor="file"
                          style={{
                            height: "38px",
                            background: " #28a745",
                            border: "1px solid #28a745",
                            color: "#ffffff",
                            fontSize: "16px",
                            borderRadius: "6px",
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            width: "max-content",
                          }}
                          // className="import"
                        >
                          <Row
                            style={{ alignItems: "center", display: "flex" }}
                          >
                            <i
                              className="pi pi-file-excel"
                              style={{ marginRight: "0.5rem" }}
                            ></i>
                            <span
                              style={{
                                color: "#ffffff",
                                fontSize: "14px",
                                fontWeight: "400",
                              }}
                            >
                              Import from excel
                            </span>
                          </Row>
                        </label> */}
                        </>
                      )}
                    </>
                  )}
              </div>
            </Row>
          </Col>
        )}
      </Row>
      <Row>
        <Table
          className="wolf-table"
          size="middle"
          loading={loading}
          dataSource={dataSource}
          rowKey={"rowIdx"}
          onChange={(pagination, filters, sorter, extra) => {
            if (extra.action === "filter") {
              setFilterState(filters);
              if (setItemsCount) setItemsCount(extra.currentDataSource.length);
            }
            setSearchedData(extra.currentDataSource);
          }}
          columns={columns}
          rowClassName={`wolf-table-row ${rowClickAction && "hover"}`}
          scroll={{ x: "max-content", y: "calc(100vh - 300px)" }}
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) => {
                if (rowClickAction && !hoverAction)
                  rowClickAction(record, rowIndex, event);
              },
            };
          }}
        />
      </Row>
    </div>
  );
};

export default DynamicTable;
